import { ClientType, GFAFullDetails } from "@dune/types";
import { defaultSocietyValues } from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { baseSocietyClientSchema } from "../brokers/constructionSites/useCreateConstructionSiteForm";
import { participationPercentageSchema } from "../../utils/zodUtils";

const realEstateSocietySchema = z.object({
	siren: z.string().refine((val) => val.length === 9 || val.length === 14, {
		message: "Le SIREN doit faire 9 ou le SIRET doit faire 14 caractères",
	}),
	denomination: z.string().min(1, "La raison sociale est obligatoire"),
});

const baseGFAShareholderInputSchema = z.object({
	type: z.enum(["DIRECT", "INDIRECT"]),
	positionInTheCompany: z.string().optional(),
	participationPercentage: z
		.string()
		.min(1, "Le pourcentage de participation est obligatoire"),
});

const gfaShareholderInputSchema = baseGFAShareholderInputSchema.and(
	z.discriminatedUnion("shareholderType", [
		z.object({
			shareholderType: z.literal("USER"),
			shareholderUser: z.object({
				firstname: z.string().min(1, "Le prénom est obligatoire"),
				lastname: z.string().min(1, "Le nom est obligatoire"),
			}),
		}),
		z.object({
			shareholderType: z.literal("SOCIETY"),
			shareholderSociety: baseSocietyClientSchema,
		}),
	]),
);

type BaseShareholderFormData = z.infer<typeof gfaShareholderInputSchema>;
const baseShareholderDefaultValues: BaseShareholderFormData = {
	shareholderType: "USER",
	shareholderUser: {
		firstname: "",
		lastname: "",
	},
	type: "DIRECT",
	positionInTheCompany: "",
	participationPercentage: "",
};

// Input schema for experiences (string inputs)
const gfaExperienceInputSchema = z.object({
	operationName: z.string().optional(),
	year: z.string().optional(),
	salePriceTTC: z.string().optional(),
	progressPercentage: z.string().optional(),
	unsoldStockValue: z.string().optional(),
	marginAmount: z.string().optional(),
});

// Input schema for form (string inputs)
const createGfaShareholdersAndExperiencesInputSchema = <
	T extends ClientType | null | undefined = undefined,
>(
	moaClientType?: T,
) =>
	z
		.object({
			isRealEstateSocietyDifferentThanMOA: z
				.string()
				.refine((data) => data === "true" || data === "false", {
					message: "Le champ est invalide",
				})
				.refine(
					(data) => {
						// If "false" is selected (promoteur same as MOA) and MOA is not a society, it's invalid
						if (data === "false" && moaClientType !== "SOCIETY") {
							return false;
						}
						return true;
					},
					{
						message: "Le promoteur ne peut pas être une personne physique",
					},
				),
			realEstateSociety: realEstateSocietySchema,
			shareholders: z.array(gfaShareholderInputSchema),
			experiences: z.array(gfaExperienceInputSchema),
		})
		.superRefine((data, ctx) => {
			if (data.isRealEstateSocietyDifferentThanMOA === "true") {
				const society = realEstateSocietySchema.safeParse(
					data.realEstateSociety,
				);
				if (!society.success) {
					for (const error of society.error.issues) {
						ctx.addIssue({
							...error,
							path: ["realEstateSociety", ...error.path],
						});
					}
				}
			}

			// Validate shareholders participationPercentage
			for (const [index, shareholder] of data.shareholders.entries()) {
				const result = participationPercentageSchema.safeParse(
					shareholder.participationPercentage,
				);
				if (!result.success) {
					for (const issue of result.error.issues) {
						ctx.addIssue({
							...issue,
							path: ["shareholders", index, "participationPercentage"],
						});
					}
				}
			}
		});

// Type générique qui dépend du paramètre moaClientType
export type GFAShareholdersAndExperiencesFormData<
	T extends ClientType | null | undefined = undefined,
> = z.infer<
	ReturnType<typeof createGfaShareholdersAndExperiencesInputSchema<T>>
>;

export type GFAShareholderFormData = z.infer<typeof gfaShareholderInputSchema>;

// On utilise le type sans paramètre de type pour les valeurs par défaut
export const gfaShareholdersAndExperiencesDefaultValues: GFAShareholdersAndExperiencesFormData<undefined> =
	{
		isRealEstateSocietyDifferentThanMOA: "false",
		realEstateSociety: {
			siren: "",
			denomination: "",
		},
		shareholders: [],
		experiences: [],
	};

export function useGFAShareholdersAndExperiencesForm<
	T extends ClientType | null | undefined = undefined,
>(gfa: GFAFullDetails | null, moaClientType?: T) {
	const inputSchema =
		createGfaShareholdersAndExperiencesInputSchema<T>(moaClientType);
	const defaultValues = gfa
		? mapGFAShareholdersAndExperiencesToFormData(gfa)
		: gfaShareholdersAndExperiencesDefaultValues;
	type FormData = z.infer<typeof inputSchema>;
	const form = useForm<FormData>({
		mode: "all",
		defaultValues,
		resolver: zodResolver(inputSchema),
	});
	const { reset } = form;
	useEffect(() => {
		gfa && reset(mapGFAShareholdersAndExperiencesToFormData(gfa));
	}, [reset, gfa]);
	return { form, defaultValues };
}

export function useGFAShareholderForm() {
	const form = useForm<BaseShareholderFormData>({
		mode: "all",
		defaultValues: baseShareholderDefaultValues,
		resolver: zodResolver(gfaShareholderInputSchema),
	});
	return { form };
}

const mapGFAShareholdersAndExperiencesToFormData = (
	gfa: GFAFullDetails,
): Partial<GFAShareholdersAndExperiencesFormData<undefined>> => {
	const shareholdersAndExperiences = gfa.gfaShareholdersAndExperiences;
	if (!shareholdersAndExperiences)
		return gfaShareholdersAndExperiencesDefaultValues;
	const { shareholders, experiences } = shareholdersAndExperiences;
	return {
		isRealEstateSocietyDifferentThanMOA:
			shareholdersAndExperiences.realEstateIsMOA ? "false" : "true",
		realEstateSociety: shareholdersAndExperiences.realEstateSociety
			? {
					siren: shareholdersAndExperiences.realEstateSociety.siren ?? "",
					denomination:
						shareholdersAndExperiences.realEstateSociety.denomination,
				}
			: undefined,
		shareholders: shareholders.map((shareholder) => {
			const base = {
				type: shareholder.type ?? undefined,
				positionInTheCompany: shareholder.positionInTheCompany ?? "",
				participationPercentage:
					shareholder.participationPercentage?.toString() ?? "",
			};
			if (shareholder.shareholderType === "USER") {
				return {
					shareholderType: shareholder.shareholderType,
					shareholderUser: {
						firstname: shareholder.shareholderUser?.firstname ?? "",
						lastname: shareholder.shareholderUser?.lastname ?? "",
					},
					shareholderSociety: undefined,
					...base,
				};
			}
			return {
				shareholderType: shareholder.shareholderType,
				shareholderUser: undefined,
				shareholderSociety: shareholder.shareholderSociety
					? {
							siren: shareholder.shareholderSociety.siren,
							denomination: shareholder.shareholderSociety.denomination,
							streetNumber:
								shareholder.shareholderSociety.headOfficeAddress
									?.streetNumber ?? undefined,
							streetName:
								shareholder.shareholderSociety.headOfficeAddress?.streetName ??
								"",
							postalCode:
								shareholder.shareholderSociety.headOfficeAddress?.postalCode ??
								"",
							city:
								shareholder.shareholderSociety.headOfficeAddress?.city ?? "",
						}
					: defaultSocietyValues,
				...base,
			};
		}),
		experiences: experiences.map((experience) => ({
			operationName: experience.operationName ?? "",
			year: experience.year?.toString() ?? "",
			salePriceTTC: experience.salePriceTTC?.toString() ?? "",
			progressPercentage: experience.progressPercentage?.toString() ?? "",
			unsoldStockValue: experience.unsoldStockValue?.toString() ?? "",
			marginAmount: experience.marginAmount?.toString() ?? "",
		})),
	};
};
